import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { useQuery } from "@apollo/client";
import PageHeader from "theme/components/common/PageHeader";
import { toast } from "react-toastify";
import apiClient from "api";
import { useIsAdmin } from "state/auth";
import { gettext } from "i18n";
import { GET_MEASUREMENT_STATIONS } from "../utils/stations";
import { GET_SENSORS } from "../utils/sensors";
import { GetMeasurementStations } from "../utils/__generated__/GetMeasurementStations";
import { GetSensors } from "../utils/__generated__/GetSensors";
import {
  ReportDetail,
  FormData,
  SensorRow,
  toLocalDatetimeLocal,
  toUtcIsoString,
  generateId,
} from "../components/offlineReports";
import { ReportEditForm } from "../components/offlineReports/ReportEditForm";

import "./ReportDetailEditPage.scss";

const initializeFormData = (report: ReportDetail): FormData => {
  const sensorRows: SensorRow[] = (report.data.sensorRows ?? []).map((row) => ({
    ...row,
    id: row.id || generateId(),
    installed: row.installed ?? false,
    customSensorName:
      row.sensorName === "Other" ? (row.customSensorName ?? "") : undefined,
  }));

  return {
    stationId: report.stationId,
    stationName: report.stationName ?? "",
    organizationName: report.organizationName ?? "",
    reportType: (report.reportType as FormData["reportType"]) || "",
    timestamp: toLocalDatetimeLocal(report.timestamp),
    responsiblePerson: report.responsiblePerson ?? "",
    addAnnotation: report.data.addAnnotation ?? false,
    sensorRows,
    batteryVoltageChecked: report.data.batteryVoltageChecked ?? false,
    batteryChanged: report.data.batteryChanged ?? false,
    moistureBagChecked: report.data.moistureBagChecked ?? false,
    dataTransferChecked: report.data.dataTransferChecked ?? false,
    otherNotesPublic: report.data.otherNotesPublic ?? "",
    otherNotesAdmin: report.data.otherNotesAdmin ?? "",
    attachments: report.attachments,
  };
};

const useReportDetail = (uuid: string) => {
  const [report, setReport] = React.useState<ReportDetail | null>(null);
  const [loading, setLoading] = React.useState(true);

  const fetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiClient.request(
        `/organization-admin/offline-report/detail/${uuid}`,
        { method: "GET" },
      );
      setReport(result as ReportDetail);
    } catch (e) {
      console.error(e);
      toast.error(gettext("Failed to load report"));
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  React.useEffect(() => {
    fetch();
  }, [fetch]);

  return { report, loading, refetch: fetch };
};

const ReportDetailEditPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const isGlobalAdmin = useIsAdmin();
  const { report, loading, refetch } = useReportDetail(uuid!);
  const [saving, setSaving] = React.useState(false);
  const [formData, setFormData] = React.useState<FormData | null>(null);

  const { data: stationsData } = useQuery<GetMeasurementStations>(
    GET_MEASUREMENT_STATIONS,
  );
  const { data: sensorsData } = useQuery<GetSensors>(GET_SENSORS);

  const stations =
    stationsData?.station.filter((s) => s.organization !== null) ?? [];
  const sensors = sensorsData?.sensor ?? [];

  React.useEffect(() => {
    if (report) {
      setFormData(initializeFormData(report));
    }
  }, [report]);

  if (!isGlobalAdmin) {
    return (
      <div className="ReportDetailEditPage">
        <div className="ReportDetailEditPage__error">
          {gettext("You do not have permission to edit this report.")}
        </div>
      </div>
    );
  }

  if (loading || !formData) {
    return (
      <div className="ReportDetailEditPage">
        <div className="ReportDetailEditPage__spinner">
          <Spinner animation="border" size="sm" className="me-2" />
          {gettext("Loading...")}
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="ReportDetailEditPage">
        <div className="ReportDetailEditPage__error">
          {gettext("Report not found.")}
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    if (!formData.stationId) {
      toast.error(gettext("Station is required"));
      return;
    }
    if (!formData.reportType) {
      toast.error(gettext("Report type is required"));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        uuid: report.uuid,
        stationId: formData.stationId,
        stationName: formData.stationName,
        organizationName: formData.organizationName,
        reportType: formData.reportType,
        status: report.status,
        responsiblePerson: formData.responsiblePerson,
        timestamp: toUtcIsoString(formData.timestamp),
        data: {
          addAnnotation: formData.addAnnotation,
          sensorRows: formData.sensorRows.map((row) => ({
            id: row.id,
            sensorId: row.sensorId,
            sensorName: row.sensorName,
            customSensorName: row.customSensorName,
            inspected: row.inspected,
            cleaned: row.cleaned,
            calibrated: row.calibrated,
            replaced: row.replaced,
            installed: row.installed,
            notes: row.notes,
          })),
          batteryVoltageChecked: formData.batteryVoltageChecked,
          batteryChanged: formData.batteryChanged,
          moistureBagChecked: formData.moistureBagChecked,
          dataTransferChecked: formData.dataTransferChecked,
          otherNotesPublic: formData.otherNotesPublic,
          otherNotesAdmin: formData.otherNotesAdmin,
        },
        attachments: formData.attachments.map((att) => ({
          uuid: att.uuid,
          filename: att.filename,
          mimeType: att.mimeType,
          url: att.url,
          isAdminOnly: att.isAdminOnly,
        })),
      };

      await apiClient.request(
        `/organization-admin/offline-report/update/${uuid}`,
        {
          method: "POST",
          data: payload,
        },
      );

      toast.success(gettext("Report saved successfully"));
      await refetch();
      navigate(`/admin/reports/${uuid}`);
    } catch (e) {
      console.error(e);
      toast.error(gettext("Failed to save report"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/reports/${uuid}`);
  };

  return (
    <div className="ReportDetailEditPage">
      <div className="ReportDetailEditPage__header">
        <div className="ReportDetailEditPage__header-actions">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleCancel}
            disabled={saving}
          >
            {gettext("Cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {gettext("Saving...")}
              </>
            ) : (
              gettext("Save changes")
            )}
          </Button>
        </div>
        <PageHeader
          title={`${gettext("Edit Report")} — ${report.stationName ?? gettext("No station")}`}
        />
      </div>

      <ReportEditForm
        formData={formData}
        onChange={setFormData}
        stations={stations}
        sensors={sensors}
      />

      {formData.attachments.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            {gettext("Attachments")} ({formData.attachments.length})
          </Card.Header>
          <Card.Body>
            <div className="ReportDetailEditPage__attachments">
              {formData.attachments.map((att, index) => (
                <div
                  key={att.uuid}
                  className="ReportDetailEditPage__attachment"
                >
                  <button
                    type="button"
                    className="ReportDetailEditPage__attachment-delete"
                    onClick={() => {
                      const updated = formData.attachments.filter(
                        (_, i) => i !== index,
                      );
                      setFormData({ ...formData, attachments: updated });
                    }}
                    title={gettext("Delete attachment")}
                  >
                    ×
                  </button>
                  {att.url && att.mimeType.startsWith("image/") ? (
                    <a href={att.url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={att.url}
                        alt={att.filename}
                        className="ReportDetailEditPage__attachment-img"
                      />
                    </a>
                  ) : (
                    <a
                      href={att.url ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ReportDetailEditPage__attachment-file"
                    >
                      {att.filename}
                    </a>
                  )}
                  <div className="ReportDetailEditPage__attachment-name">
                    {att.filename}
                  </div>
                  <Form.Check
                    type="checkbox"
                    id={`admin-only-${att.uuid}`}
                    label={gettext("Admin only")}
                    checked={att.isAdminOnly ?? false}
                    onChange={(e) => {
                      const updated = [...formData.attachments];
                      updated[index] = {
                        ...att,
                        isAdminOnly: e.target.checked,
                      };
                      setFormData({ ...formData, attachments: updated });
                    }}
                    className="ReportDetailEditPage__attachment-admin-check"
                  />
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      <div className="ReportDetailEditPage__footer-actions">
        <Button
          variant="primary"
          size="sm"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {gettext("Saving...")}
            </>
          ) : (
            gettext("Save changes")
          )}
        </Button>
      </div>
    </div>
  );
};

export default ReportDetailEditPage;
